import type { ApolloQueryResult } from '@apollo/client';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { hasAddedLines } from '@/utils/checkout';
import { getLabel } from '@/utils/labelUtils';

import { CMSSection } from '@/cms/CMSSection';
import type { BreadcrumbItem } from '@/components/Breadcrumb';
import { Button4Gift } from '@/components/Button4Gift/Button4Gift';
import { EngravingModal } from '@/components/EngravingModal/EngravingModal';
import { OrSeparator } from '@/components/ORseparator/ORseparator';
import { Price } from '@/components/Price';
import { ProductDetail } from '@/components/ProductDetail';
import { useStickyPurchaseBar } from '@/components/ProductDetail/components';
import { ServicePerks } from '@/components/ServicePerks';
import { View3DButton, Viewer3DModal } from '@/components/Viewer3D';
import { Visibility } from '@/components/Visibility';
import type {
  ContentfulPlaceholderProductDetailsFragment,
  ContentfulSelectorModalByMarketingPartnerQuery,
} from '@/graphql/contentful/api';
import type {
  OrderAppCtaType,
  OrderAppProductVariantBusinessDetailsFragment,
} from '@/graphql/orderApp/api';
import type {
  ProductsByMarketingPartnerQuery,
  ProductsBySkusQuery,
  ProductVariantWatchBasicDetailsFragment,
  ProductVariantWatchDetailsFragment,
} from '@/graphql/saleor/api';
import { useCheckout } from '@/hooks/useCheckout';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useIsEngravingActivated } from '@/hooks/useIsEngravingActivated';
import { useNativeCall } from '@/hooks/useNativeCall';
import { useRouter } from '@/hooks/useRouter';
import { useSendAddedToCartAlgoliaEvent } from '@/hooks/useSendAlgoliaEvent';
import { filterEnabledStraps } from '@/integrations/algolia/helpers';
import { getMultiselectValue } from '@/lib/attributes';
import {
  DEFAULT_STRAP_PREVIEW_COORDS,
  parseSaleorCropCoordinates,
  selectStrapPreviewMedia,
} from '@/lib/cdn/cropTransform';
import { SALEOR_PRE_ORDER_CONFIG_KEY } from '@/lib/const';
import { captureError, isAbortLikeError } from '@/lib/errors';
import { EVENT_NAME } from '@/lib/gtm/const';
import {
  getCollectionTranslatedName,
  getProductTranslatedName,
} from '@/lib/i18n';
import { isOricoFinancingAvailable } from '@/lib/payment/oricoFinancing';
import { isSmbcAvailable } from '@/lib/payment/smbc';
import { getPreOrderConfig } from '@/lib/preOrder';
import {
  getProductPath,
  getProductType,
  isProductPersonalizedEngraving,
} from '@/lib/product';
import { fetchProductsBySkus } from '@/lib/product/api';
import { getGqlRegionFromLocale, getTaxMessageShort } from '@/lib/regions';
import { generateDataTestId } from '@/lib/tests';
import type { Maybe } from '@/lib/types';
import { commonMessages } from '@/messages/common';
import { useCommonModalProviderContext } from '@/providers/CommonModalProvider';
import {
  isModuleActive,
  Modules,
  useConfigProvider,
} from '@/providers/ConfigProvider';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { useRegionProvider } from '@/providers/RegionProvider';
import { getWatchPdpBreadcrumbs } from '@/views/WatchView/getWatchPdpBreadcrumbs';

import { IS_3D_VIEWER_ENABLED, SKU_TO_3D_MODEL } from '../constants';
import { AvailabilityRequestModal } from './AvailabilityRequestModal';
import { BookAppointmentModal } from './BookAppointmentModal';
import { BoutiqueDeliveryInformation } from './BoutiqueDeliveryInformation/BoutiqueDeliveryInformation';
import { BoutiqueDeliveryModal } from './BoutiqueDeliveryModal/BoutiqueDeliveryModal';
import ChangeStrapButton from './ChangeStrapButton/ChangeStrapButton';
import ChangeStrapModal from './ChangeStrapModal/ChangeStrapModal';
import { FinanceCalculatorModal } from './FinanceCalculatorModal/FinanceCalculatorModal';
import { loadAffirm } from './loadAffirm';
import MarketingSelectorButton from './MarketingSelectorButton/MarketingSelectorButton';
import MarketingSelectorModal from './MarketingSelectorModal/MarketingSelectorModal';
import { OneyModal } from './OneyModal/OneyModal';
import { PreOrderInformation } from './PreOrderInformation/PreOrderInformation';
import {
  buildSecondaryCtaItems,
  isSecondaryNotifyMeVisible,
} from './ProductDetailSection.helpers';
import { SendGiftModal } from './SendGiftModal/SendGiftModal';

import * as S from './ProductDetailSection.styles';

export interface ProductDetailSectionOpts {
  businessData: Maybe<OrderAppProductVariantBusinessDetailsFragment>;
  marketingPartnerProductsResult: ApolloQueryResult<ProductsByMarketingPartnerQuery>;
  marketingSelectorModalResult:
    | NonNullable<
        ContentfulSelectorModalByMarketingPartnerQuery['selectorCollection']
      >['items'][0]
    | null;
  variantBasicDetails: ProductVariantWatchBasicDetailsFragment;
  variantDetails: Maybe<ProductVariantWatchDetailsFragment>;
}

export interface BundleVariationOption {
  alt: string | null | undefined;
  coords: { x1: number; x2: number; y1: number; y2: number };
  image: string | undefined;
  slug: string | null | undefined;
  strapColor: string | null | undefined;
  strapColorCode: string | null | undefined;
  strapMaterial: string | null | undefined;
}

export type ProductDetailSectionProps =
  ContentfulPlaceholderProductDetailsFragment & ProductDetailSectionOpts;

export const ProductDetailSection = ({
  businessData,
  marketingPartnerProductsResult,
  marketingSelectorModalResult,
  perksCollection,
  variantBasicDetails: { id, metadata, product, sku },
  variantDetails,
}: ProductDetailSectionProps) => {
  const { country, locale, productCatalogMode } = useRegionProvider();
  const isEngravingActivated = useIsEngravingActivated();
  const { locale: routerLocale } = useRouter();
  const { channel } = getGqlRegionFromLocale(routerLocale);
  const taxMessage = getTaxMessageShort(country.channelSlug);
  const { moduleActivations, smbcPaymentMax, smbcPaymentMin } =
    useConfigProvider();
  const isBoutiquePickUpAvailable = isModuleActive(
    moduleActivations,
    Modules.BOUTIQUE_PICKUP,
  );
  const is4GiftBtnAvailable = isModuleActive(
    moduleActivations,
    Modules.FOR_GIFT,
  );

  const isAffirmModuleAvailable = isModuleActive(
    moduleActivations,
    Modules.AFFIRM,
  );
  const isCembraMoudleAvailable = isModuleActive(
    moduleActivations,
    Modules.PDP_CEMBRA_FINANCING,
  );
  const isOneyModuleAvailable = isModuleActive(
    moduleActivations,
    Modules.PDP_ONEY_3X,
  );
  const isAgosModuleAvailable = isModuleActive(
    moduleActivations,
    Modules.PDP_AGOS,
  );
  const isCreditPlusAvailable = isModuleActive(
    moduleActivations,
    Modules.PDP_CREDIT_PLUS,
  );
  const isZipModuleAvailable = isModuleActive(moduleActivations, Modules.ZIP);
  const isYourAssetModuleAvailable = isModuleActive(
    moduleActivations,
    Modules.YOUR_ASSET,
  );

  const isOricoModuleAvailable = isModuleActive(
    moduleActivations,
    Modules.ORICO_FINANCING,
  );

  const isSmbcModuleAvailable = isModuleActive(moduleActivations, Modules.SMBC);

  const isAvailabilityRequestAvailable =
    isModuleActive(moduleActivations, Modules.AVAILABILITY_REQUEST) &&
    productCatalogMode;

  const financeCalculatorPayment = Object.entries({
    agos: isAgosModuleAvailable,
    cembra: isCembraMoudleAvailable,
    creditPlus: isCreditPlusAvailable,
  }).find(([, value]) => value)?.[0];

  const isNotifyMe: boolean = getPreOrderConfig(
    metadata,
    SALEOR_PRE_ORDER_CONFIG_KEY.NotifyMe,
    country.code,
  );

  const [isBookAppointmentModalOpen, setIsBookAppointmentModalOpen] =
    useState(false);
  const [isChangeStrapModalOpen, setIsChangeStrapModalOpen] = useState(false);
  const [isMarketingSelectorModalOpen, setIsMarketingSelectorModalOpen] =
    useState(false);
  const [isBoutiqueDeliveryModalOpen, setIsBoutiqueDeliveryModalOpen] =
    useState(false);
  const [is4GiftsAvailable, setIs4GiftsAvailable] = useState(false);
  const [isFinanceCalculatorOpen, setIsFinanceCalculatorOpen] = useState(false);
  const [isOneyModalOpen, setIsOneyModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [enabledStrapSkus, setEnabledStrapSkus] = useState<string[]>([]);
  const [isAvailabilityRequestModalOpen, setIsAvailabilityRequestModalOpen] =
    useState(false);
  const [is3DViewerModalOpen, setIs3DViewerModalOpen] = useState(false);
  const [isEngravingModalOpen, setIsEngravingModalOpen] = useState(false);
  const [variantCTA, setVariantCTA] = useState<OrderAppCtaType>();
  const { openNotifyMe, setContactUsModalStatus } =
    useCommonModalProviderContext();
  const {
    lineAdd,
    snapshot: { isProcessing },
  } = useCheckout();

  const canBeAddedToBag =
    variantCTA && ['ORDER', 'PRE_ORDER'].includes(variantCTA);

  // BEC-24431: sticky purchase bar shown once the on-page CTA scrolls off
  // screen. Gate mirrors the on-page CTA (which also requires pricing).
  const { ctaRef, visible: showStickyBar } = useStickyPurchaseBar({
    cta: variantCTA,
    hasPricing: Boolean(variantDetails?.pricing),
    productCatalogMode,
  });

  const { initiateNativeCall } = useNativeCall(country.code);
  const { formatMessage } = useIntl();

  const { sendEcommerceEventBySkus, sendEvent } = useGoogleAnalytics();

  const sendAddedToCartAlgoliaEvent = useSendAddedToCartAlgoliaEvent();

  useEffect(() => {
    if (businessData?.variantCTA) {
      setVariantCTA(businessData.variantCTA);
    }
  }, [businessData?.variantCTA]);

  const collection = product?.collections?.[0];
  let collectionName = getCollectionTranslatedName(product);
  collectionName = collectionName ? collectionName.trim() : '';
  const name = getProductTranslatedName(product);
  const productNameWithCollection = !name?.includes(collectionName?.trim())
    ? (collectionName + ' ' + name).trim()
    : name;

  const productType = getProductType(product.productType);
  const isCallToBuyAvailable = Boolean(
    businessData?.phoneCTA === 'ORDER' &&
    variantDetails?.quantityAvailable &&
    variantDetails?.quantityAvailable > 0,
  );

  const isCallToPreOrderAvailable = Boolean(
    businessData?.phoneCTA === 'PRE_ORDER' &&
    variantDetails?.quantityAvailable &&
    variantDetails?.quantityAvailable <= 0,
  );

  const getDistributionBCode = () => {
    const firstValue = product?.distributionStatus?.values?.[0];
    const externalRef = firstValue?.externalReference;
    if (externalRef && externalRef.includes('-')) {
      return externalRef.split('-')[1];
    }
    return null;
  };

  const distributionBCode = getDistributionBCode();

  // BEC-19123: Display "Notify me" CTA for two specific cases:
  // CASE A: Sellable WEB = YES, Stock = NO, Distribution status = B2
  // CASE B: Sellable WEB = YES, Stock = NO, Distribution status = B4
  // Additional business rules: isNotifyMe master switch must be enabled, not in catalog mode
  const isSecondaryNotifyMeAvailable = isSecondaryNotifyMeVisible({
    businessData,
    distributionBCode,
    isNotifyMe,
    productCatalogMode,
    variantCTA,
  });

  let breadcrumbs: BreadcrumbItem[] = [];
  breadcrumbs = getWatchPdpBreadcrumbs(
    formatMessage,
    collectionName,
    collection!.slug,
    name,
  );

  const callToBuy = () => {
    if (sku) {
      sendEcommerceEventBySkus({
        event: EVENT_NAME.CALL_TO_BUY,
        skus: [sku],
      });
    }
    initiateNativeCall();
  };

  const SECONDARY_CTA_DATA = buildSecondaryCtaItems({
    is4GiftsAvailable,
    isAvailabilityRequestAvailable,
    isCallToBuyAvailable,
    isCallToPreOrderAvailable,
    isSecondaryNotifyMeAvailable,
    onAvailabilityRequest: () => setIsAvailabilityRequestModalOpen(true),
    onBookAppointment: () => {
      sendEvent({ event: EVENT_NAME.INTENTION_BOOK_APPT });
      setIsBookAppointmentModalOpen(true);
    },
    onCallToBuy: () => callToBuy(),
    onGiveAsAGift: () => setIsModalOpen(true),
    onNotifyMe: () => {
      if (sku) openNotifyMe({ sku });
    },
    productCatalogMode,
    variantCTA,
  });

  const handleAddToCart = async () => {
    if (canBeAddedToBag && variantCTA) {
      const addedLines = await lineAdd(id, variantCTA);
      if (sku && hasAddedLines(addedLines)) {
        sendAddedToCartAlgoliaEvent(
          sku,
          variantDetails?.pricing?.price?.gross.amount,
          variantDetails?.pricing?.price?.gross.currency,
        );
      }
    } else if (variantCTA === 'NOTIFY') {
      if (sku) openNotifyMe({ sku });
    } else {
      setContactUsModalStatus(true, sku);
    }
  };

  const check4Gift = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_4GIFT_URL_ENDPOINT}/api/${process.env.NEXT_PUBLIC_4GIFT_URL_VERSION}/breitling/products-4gift-availability?skus[]=${sku}&channel=${channel}`,
          { signal },
        );
        const data = await res.json();
        if (data?.availabilities && data.availabilities.length > 0 && sku) {
          setIs4GiftsAvailable(data.availabilities[0][sku]);
        }
      } catch (err) {
        if (isAbortLikeError(err)) {
          return;
        }
        captureError(err as Error);
        setIs4GiftsAvailable(false);
      }
    },
    [sku, channel],
  );

  useEffect(() => {
    if (variantCTA === 'ORDER' && is4GiftBtnAvailable) {
      const controller = new AbortController();
      check4Gift(controller.signal);
      return () => controller.abort();
    }
  }, [variantCTA, is4GiftBtnAvailable, check4Gift]);

  const marketingStatus = getMultiselectValue(
    variantDetails?.product.marketingStatus,
    false,
  );

  const marketingPartner = getMultiselectValue(
    variantDetails?.product.marketingPartner,
    false,
  );

  // we need to have a marketing partner and more than one marketing partner product to show the modal, otherwise there are no other products to switch to
  const shouldShowMarketingSelectorModal =
    marketingPartner?.length > 0 &&
    marketingPartnerProductsResult?.data?.products?.edges &&
    marketingPartnerProductsResult.data.products.edges.length > 1 &&
    marketingSelectorModalResult;

  const launchDate = variantDetails?.product?.launchDate?.values?.[0]
    ?.name as string;

  const label = getLabel(marketingStatus, launchDate, true);

  const { query } = useRegionProvider();

  const bundleVariations = useMemo(
    () => getMultiselectValue(variantDetails?.product?.bundleVariations),
    [variantDetails?.product?.bundleVariations],
  );
  const draftMode = useContext(DraftModeContext);
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!bundleVariations || bundleVariations.length === 0) {
          if (!cancelled) setEnabledStrapSkus([]);
          return;
        }
        const skus = await filterEnabledStraps(
          country,
          locale.code,
          bundleVariations,
          draftMode,
        );
        if (!cancelled) setEnabledStrapSkus(skus);
      } catch {
        if (!cancelled) setEnabledStrapSkus([]);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [bundleVariations, country, locale.code, draftMode]);

  const [data, setData] = useState<ProductsBySkusQuery | null>(null);
  const isActiveFetchProductsRef = useRef(true);
  useEffect(() => {
    isActiveFetchProductsRef.current = true;
    const fetchData = async () => {
      const result = await fetchProductsBySkus({
        skus: enabledStrapSkus,
        languageCode: query.languageCode,
      });
      if (!isActiveFetchProductsRef.current) return;
      if (result) {
        setData(result);
      }
    };

    fetchData();
    return () => {
      isActiveFetchProductsRef.current = false;
    };
  }, [enabledStrapSkus, query.languageCode]);

  const strapPreviewMedia = selectStrapPreviewMedia(product.media);
  const strapPreviewCoords = parseSaleorCropCoordinates(
    strapPreviewMedia?.strapPreviewCoordinates,
    DEFAULT_STRAP_PREVIEW_COORDS,
  );
  // Keep the Saleor URL variant as-is (no _512 => _4096 rewrite), since
  // forcing larger variants can return 403 for some products/environments.
  const strapPreviewImage = strapPreviewMedia?.url ?? '';

  const bundleVariationOptions: BundleVariationOption[] = [
    {
      coords: strapPreviewCoords,
      image: strapPreviewImage,
      strapColor: product.strapColor?.values[0]?.translation?.plainText,
      strapColorCode: product.strapColor?.values[0]?.translation?.name,
      strapMaterial: product.strapMaterial?.values[0]?.translation?.plainText,
      slug: product.slug,
      alt: name,
    },
  ];

  data?.products?.edges?.map((value) => {
    bundleVariationOptions.push({
      coords: strapPreviewCoords,
      image: value.node.thumbnail?.url ?? '',
      strapColor: value.node.strapColor?.values[0]?.translation?.plainText,
      strapColorCode: value.node.strapColor?.values[0]?.translation?.name,
      strapMaterial:
        value.node.strapMaterial?.values[0]?.translation?.plainText,
      alt: value.node.thumbnail?.alt,
      slug: value.node.slug,
    });
  });

  const useAffirm = process.env.NEXT_PUBLIC_USE_AFFIRM === 'true';

  useEffect(() => {
    if (isAffirmModuleAvailable) {
      let affirmKey, affirmUrl;

      switch (country.code.toLowerCase()) {
        case 'ca':
          affirmKey = process.env.NEXT_PUBLIC_AFFIRM_KEY_CA ?? '';
          affirmUrl = process.env.NEXT_PUBLIC_AFFIRM_URL_CA ?? '';
          break;
        default:
          affirmKey = process.env.NEXT_PUBLIC_AFFIRM_KEY_US ?? '';
          affirmUrl = process.env.NEXT_PUBLIC_AFFIRM_URL_US ?? '';
          break;
      }
      loadAffirm(affirmKey, affirmUrl);
    }
  }, [
    locale,
    useAffirm,
    variantDetails?.pricing?.price?.gross?.amount,
    isAffirmModuleAvailable,
    isZipModuleAvailable,
    variantCTA,
  ]);

  const shareableProductData = {
    sku: product.defaultVariant?.sku,
    name: product.name,
    collection: product.collections?.[0]?.name,
    image: product.media?.[0]?.url,
    url: [
      process.env.NEXT_PUBLIC_URL,
      country.code.toLowerCase(),
      '-',
      locale.slug,
      getProductPath(product, true),
    ].join(''),
  };

  useEffect(() => {
    if (sku) {
      sendEcommerceEventBySkus({
        event: EVENT_NAME.VIEW_ITEM,
        skus: [sku],
      });
    }
  }, [sku]);

  const defaultImageAlt = `${product.name} - ${sku}`;
  const shouldShowBoutiquePickup =
    isBoutiquePickUpAvailable && !productCatalogMode && canBeAddedToBag;

  const isSmbcSentenceAvailable =
    isSmbcModuleAvailable &&
    isSmbcAvailable(
      country.code,
      variantCTA,
      variantDetails?.pricing?.price?.gross?.amount ?? 0,
      smbcPaymentMax,
      smbcPaymentMin,
    );

  const isOricoSentenceAvailable =
    isOricoModuleAvailable &&
    isOricoFinancingAvailable(country.code, variantCTA) &&
    !isSmbcSentenceAvailable;

  const enabledEngraving =
    isEngravingActivated && isProductPersonalizedEngraving(product);

  return (
    <CMSSection
      {...generateDataTestId('productDetail', 'section')}
      data-test-id='ProductDetailSection'
    >
      <ProductDetail>
        <ProductDetail.ImagesGallery
          alt={defaultImageAlt}
          compactOnMobile
          data-test-id='ProductDetailImagesGallery'
          gallery='watchPdpCarousel'
          media={product.media}
          mediaLarge={product.mediaLarge}
          name={name}
          price={variantDetails?.pricing?.price?.gross.amount}
          productType={productType}
          shareableProductData={shareableProductData}
          sku={sku}
          tag={label && <FormattedMessage {...label} />}
          theCurrency={variantDetails?.pricing?.price?.gross.currency}
        >
          <ProductDetail.Breadcrumbs
            data-test-id='ProductDetailBreadcrumbs'
            items={breadcrumbs}
            mdHidden
          />
          <Visibility mdHidden>
            <S.ButtonWrapper>
              <div style={{ display: 'inline-flex', flexDirection: 'row' }}>
                {IS_3D_VIEWER_ENABLED && sku && SKU_TO_3D_MODEL[sku] && (
                  <View3DButton
                    data-testid='View3DButton'
                    onOpen={() => setIs3DViewerModalOpen(true)}
                  />
                )}
                {enabledStrapSkus?.length > 0 && (
                  <div style={{ marginLeft: '1em' }}>
                    <ChangeStrapButton
                      coords={strapPreviewCoords}
                      data-testid='ChangeStrapButton'
                      name={name}
                      setIsChangeStrapModalOpen={setIsChangeStrapModalOpen}
                      url={strapPreviewImage}
                    />
                  </div>
                )}
                {shouldShowMarketingSelectorModal && (
                  <div style={{ marginLeft: '1em' }}>
                    <MarketingSelectorButton
                      data-testid='MarketingSelectorButton'
                      marketingPartnerProductsResult={
                        marketingPartnerProductsResult
                      }
                      marketingSelectorModalResult={
                        marketingSelectorModalResult
                      }
                      setIsMarketingSelectorModalOpen={
                        setIsMarketingSelectorModalOpen
                      }
                      sku={sku}
                    />
                  </div>
                )}
              </div>
            </S.ButtonWrapper>
          </Visibility>
        </ProductDetail.ImagesGallery>

        <ProductDetail.DetailsCard data-test-id='ProductDetailDetailsCard'>
          <ProductDetail.Breadcrumbs
            data-test-id='ProductDetailBreadcrumbs'
            items={breadcrumbs}
            mdVisible
            withJsonLd={false}
          />

          <ProductDetail.Sku $compactOnMobile data-test-id='ProductDetailSku'>
            {sku}
          </ProductDetail.Sku>
          <ProductDetail.Name $compactOnMobile data-test-id='ProductDetailName'>
            {productNameWithCollection}
          </ProductDetail.Name>
          {variantDetails?.pricing?.price?.gross && (
            <ProductDetail.Pricing
              $compactOnMobile
              data-test-id='ProductDetailPricing'
            >
              <Price price={variantDetails?.pricing?.price?.gross} size='xl' />
              <ProductDetail.PriceInformation
                data-test-id='ProductDetailPriceInformation'
                loading={!variantDetails}
              />

              <ProductDetail.TaxLabel data-test-id='ProductDetailTaxLabel'>
                {taxMessage && formatMessage(taxMessage)}
              </ProductDetail.TaxLabel>
            </ProductDetail.Pricing>
          )}
          {!productCatalogMode && (
            <ProductDetail.FinancingGroup
              $compactOnMobile
              data-test-id='ProductDetailFinancingGroup'
            >
              {useAffirm && isAffirmModuleAvailable && (
                <ProductDetail.AffirmSentence
                  amount={
                    (variantDetails?.pricing?.price?.gross?.amount ?? 0) * 100
                  }
                  className='affirm-as-low-as'
                  data-page-type='product'
                  data-test-id='ProductDetailAffirmSentence'
                  variantCTA={variantCTA}
                ></ProductDetail.AffirmSentence>
              )}
              {isZipModuleAvailable && (
                <ProductDetail.ZipWidget
                  countryCode={country.code}
                  price={variantDetails?.pricing?.price?.gross ?? null}
                  variantCTA={variantCTA}
                />
              )}
              {isYourAssetModuleAvailable && sku && (
                <ProductDetail.YourAssetWidget
                  language={locale.slug}
                  price={variantDetails?.pricing?.price?.gross ?? null}
                  sku={sku}
                  variantCTA={variantCTA}
                />
              )}
              {isCembraMoudleAvailable && (
                <ProductDetail.CembraSentence
                  onLearnMore={() => setIsFinanceCalculatorOpen(true)}
                  price={variantDetails?.pricing?.price?.gross ?? null}
                  variantCTA={variantCTA}
                />
              )}
              {isOneyModuleAvailable && (
                <ProductDetail.OneySentence
                  onOneyClick={() => setIsOneyModalOpen(true)}
                  price={variantDetails?.pricing?.price?.gross ?? null}
                  variantCTA={variantCTA}
                />
              )}
              {isAgosModuleAvailable && (
                <ProductDetail.AgosSentence
                  onCalculateFinancing={() => setIsFinanceCalculatorOpen(true)}
                  price={variantDetails?.pricing?.price?.gross ?? null}
                  variantCTA={variantCTA}
                />
              )}
              {isCreditPlusAvailable && (
                <ProductDetail.CreditPlusSentence
                  onLearnMore={() => setIsFinanceCalculatorOpen(true)}
                  price={variantDetails?.pricing?.price?.gross ?? null}
                  variantCTA={variantCTA}
                />
              )}
              <ProductDetail.V12Sentence
                amount={variantDetails?.pricing?.price?.gross?.amount ?? 0}
                country={country.code}
                currency={variantDetails?.pricing?.price?.gross.currency ?? ''}
                isLoading={!businessData}
                isProcessing={isProcessing}
                onAddToCart={handleAddToCart}
                variantCTA={variantCTA}
              />
              {isOricoSentenceAvailable && (
                <ProductDetail.OricoFinancing
                  amount={variantDetails?.pricing?.price?.gross?.amount ?? 0}
                  country={country.code}
                  variantCTA={variantCTA}
                />
              )}
              {isSmbcSentenceAvailable && <ProductDetail.Smbc />}
            </ProductDetail.FinancingGroup>
          )}
          <S.DesktopIconRow>
            {IS_3D_VIEWER_ENABLED && sku && SKU_TO_3D_MODEL[sku] && (
              <Visibility mdVisible>
                <View3DButton
                  data-testid='View3DButton'
                  onOpen={() => setIs3DViewerModalOpen(true)}
                />
              </Visibility>
            )}
            {enabledStrapSkus?.length > 0 && (
              <div style={{ marginLeft: '1em', marginRight: '1em' }}>
                <Visibility mdVisible>
                  <ChangeStrapButton
                    coords={strapPreviewCoords}
                    data-testid='ChangeStrapButton'
                    name={name}
                    setIsChangeStrapModalOpen={setIsChangeStrapModalOpen}
                    url={strapPreviewImage}
                  />
                </Visibility>
              </div>
            )}

            {shouldShowMarketingSelectorModal && (
              <Visibility mdVisible>
                <MarketingSelectorButton
                  data-testid='MarketingSelectorButton'
                  marketingPartnerProductsResult={
                    marketingPartnerProductsResult
                  }
                  marketingSelectorModalResult={marketingSelectorModalResult}
                  setIsMarketingSelectorModalOpen={
                    setIsMarketingSelectorModalOpen
                  }
                  sku={sku}
                />
              </Visibility>
            )}
          </S.DesktopIconRow>

          {shouldShowBoutiquePickup && (
            <ProductDetail.BoutiquePickUp
              $compactOnMobile
              data-test-id='BoutiquePickupWrapper'
            >
              {formatMessage(commonMessages.pdpBoutiqueDeliveryAvailableLabel)}
              <BoutiqueDeliveryInformation
                loading={!variantDetails}
                onClick={() => setIsBoutiqueDeliveryModalOpen(true)}
              />
            </ProductDetail.BoutiquePickUp>
          )}

          {/* BEC-24926: no OR separator between the CTAs; the gap tightens to
              8px on mobile only (see CtaStack). */}
          <S.CtaStack flexDirection='column' gridGap={2}>
            {!productCatalogMode && variantDetails?.pricing && (
              <>
                {enabledEngraving && variantCTA === 'ORDER' && (
                  <ProductDetail.CTA
                    cta='ENGRAVING'
                    data-test-id='ProductDetailEngravingCTA'
                    isProcessing={isProcessing}
                    loading={!businessData}
                    onClick={() => setIsEngravingModalOpen(true)}
                    variant='outlined'
                  />
                )}
                {/* Observe only the primary purchase CTA (consistent with
                    Eyewear/Strap) so the sticky bar triggers on the same
                    element across all PDP types. */}
                <div ref={ctaRef}>
                  <ProductDetail.CTA
                    cta={variantCTA}
                    data-test-id='ProductDetailAddToBagCTA'
                    isProcessing={isProcessing}
                    loading={!businessData}
                    onClick={handleAddToCart}
                  />
                </div>
              </>
            )}

            {/* BEC-24926: on mobile Call to buy takes the grey button directly
                under Add to bag. Desktop keeps its existing arrangement below,
                so both are rendered and one is hidden per breakpoint - the two
                use different element types in different containers, which CSS
                alone cannot reorder. Kept outside the pricing guard above so
                the visibility rule matches the original secondary link. */}
            {isCallToBuyAvailable && !productCatalogMode && (
              <S.MobileOnly>
                <Button4Gift onClick={callToBuy}>
                  {formatMessage(commonMessages.callToBuy)}
                </Button4Gift>
              </S.MobileOnly>
            )}
          </S.CtaStack>

          {/* Desktop keeps the original OR + 4Gift button, in its original
              position outside the CTA stack so its spacing is unchanged. */}
          {is4GiftsAvailable && variantCTA === 'ORDER' && (
            <S.DesktopOnly>
              <OrSeparator />
              <Button4Gift onClick={() => setIsModalOpen(true)} />
            </S.DesktopOnly>
          )}
          {variantCTA === 'PRE_ORDER' && <PreOrderInformation />}
          <ProductDetail.SecondaryCTA
            data-test-id='ProductDetailSecondaryCTA'
            items={SECONDARY_CTA_DATA}
          />
          <BookAppointmentModal
            data-test-id='BookAppointmentModal'
            isOpen={isBookAppointmentModalOpen}
            name={name}
            onClose={() => {
              setIsBookAppointmentModalOpen(false);
            }}
            product={product}
            sku={sku}
            variantDetails={variantDetails}
          />
          <ChangeStrapModal
            bundleVariationOptions={bundleVariationOptions}
            close={() => setIsChangeStrapModalOpen(false)}
            data-test-id='ChangeStrapModal'
            isOpen={isChangeStrapModalOpen}
            onClose={() => {
              setIsChangeStrapModalOpen(false);
            }}
          />
          <MarketingSelectorModal
            close={() => setIsMarketingSelectorModalOpen(false)}
            data-test-id='MarketingSelectorModal'
            isOpen={isMarketingSelectorModalOpen}
            marketingPartnerProductsResult={marketingPartnerProductsResult}
            marketingSelectorModalResult={marketingSelectorModalResult}
            onClose={() => setIsMarketingSelectorModalOpen(false)}
          />
          <EngravingModal
            isOpen={isEngravingModalOpen}
            onClose={() => setIsEngravingModalOpen(false)}
            product={product}
            variantCTA={variantCTA}
            variantId={id}
          />
          {!productCatalogMode && (
            <>
              <ServicePerks
                data-test-id='ServicePerksDesktop'
                enabledEngraving={enabledEngraving}
                mdVisible
                perksCollection={perksCollection}
                variant='desktop'
              />
              <ServicePerks
                data-test-id='ServicePerksMobile'
                enabledEngraving={enabledEngraving}
                mdHidden
                perksCollection={perksCollection}
                variant='mobile'
              />
            </>
          )}
        </ProductDetail.DetailsCard>
        <SendGiftModal
          country={country.code}
          isOpen={isModalOpen}
          locale={locale.slug}
          onClose={() => setIsModalOpen(false)}
          sku={sku}
        />
        <BoutiqueDeliveryModal
          isOpen={isBoutiqueDeliveryModalOpen}
          onClose={() => {
            setIsBoutiqueDeliveryModalOpen(false);
          }}
        >
          {formatMessage(commonMessages.pdpBoutiqueDeliveryAvailableModal)}
        </BoutiqueDeliveryModal>
        {financeCalculatorPayment && (
          <FinanceCalculatorModal
            handleAddToCart={handleAddToCart}
            isOpen={isFinanceCalculatorOpen}
            isProcessing={isProcessing}
            loading={!businessData}
            onClose={() => setIsFinanceCalculatorOpen(false)}
            paymentType={financeCalculatorPayment as any}
            price={variantDetails?.pricing?.price?.gross ?? null}
            variantCTA={variantCTA}
          />
        )}
        <OneyModal
          isOpen={isOneyModalOpen}
          onClose={() => setIsOneyModalOpen(false)}
          price={variantDetails?.pricing?.price?.gross ?? null}
        />
        <AvailabilityRequestModal
          data-test-id='availabilityRequestModal'
          isOpen={isAvailabilityRequestModalOpen}
          onClose={() => {
            setIsAvailabilityRequestModalOpen(false);
          }}
          price={variantDetails?.pricing?.price?.gross ?? null}
          product={product}
        />
      </ProductDetail>
      {IS_3D_VIEWER_ENABLED && sku && SKU_TO_3D_MODEL[sku] && (
        <Viewer3DModal
          isOpen={is3DViewerModalOpen}
          modelName={SKU_TO_3D_MODEL[sku]}
          onClose={() => setIs3DViewerModalOpen(false)}
        />
      )}
      <ProductDetail.StickyPurchaseBar
        cta={variantCTA}
        image={product.media?.[0]}
        isProcessing={isProcessing}
        loading={!businessData}
        name={productNameWithCollection}
        onClick={handleAddToCart}
        price={variantDetails?.pricing?.price?.gross}
        visible={showStickyBar}
      />
    </CMSSection>
  );
};
